import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SeoBrowserStore } from '@argo/seo';
import { Meta, Title } from '@angular/platform-browser';
import { 
  ScaModuleTitleComponent,
  ScaBadgeComponent,
  ScaAlertComponent,
  ScaIconComponent,
  ScaClaimCardComponent,
  ScaViewMoreComponent
} from '@argo-dev-components-sca-angular/dev-components-sca';

@Component({
  selector: 'app-seo-test',
  standalone: true,
  imports: [
    CommonModule,
    ScaModuleTitleComponent,
    ScaBadgeComponent,
    ScaAlertComponent,
    ScaIconComponent,
    ScaClaimCardComponent,
    ScaViewMoreComponent
  ],
  template: `
    <div class="container">
      <sca-module-title class="title-test">
        <h1>SEO Playground</h1>
        <span scaModuleTitlePost>Gestión de meta tags para SEO - Testing @argo/seo</span>
      </sca-module-title>

      <!-- Estado del Store -->
      <section class="card">
        <h2>Estado del Store</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Los meta tags se cargan automáticamente desde /assets/seo/seo.json basándose en la ruta actual
        </sca-alert>

        <div class="utilities-grid">
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="timeline" size="32px" />
            <span scaCardClaimTitle>Fase Actual</span>
            <span scaCardClaimDate>
              <sca-badge variant="accent">{{ seoStore.phase$() }}</sca-badge>
            </span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="settings" size="32px" />
            <span scaCardClaimTitle>Configuración</span>
            <span scaCardClaimDate>
              <sca-badge [variant]="seoStore.config$() ? 'success' : 'error'">
                {{ seoStore.config$() ? 'CARGADA' : 'NO CARGADA' }}
              </sca-badge>
            </span>
          </sca-card-claim>
        </div>
      </section>

      <!-- Meta Tags Actuales -->
      <section class="card">
        <h2>Meta Tags Actuales</h2>
        
        <div class="utilities-grid">
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="title" size="32px" />
            <span scaCardClaimTitle>Título de la Página</span>
            <span scaCardClaimDate>{{ currentTitle() }}</span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="description" size="32px" />
            <span scaCardClaimTitle>Meta Description</span>
            <span scaCardClaimDate>{{ getMetaContent('description') || 'No establecida' }}</span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="sell" size="32px" />
            <span scaCardClaimTitle>Meta Keywords</span>
            <span scaCardClaimDate>{{ getMetaContent('keywords') || 'No establecidas' }}</span>
          </sca-card-claim>
        </div>
      </section>

      <!-- Stores Disponibles -->
      <section class="card">
        <h2>Stores Disponibles</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Dos stores para diferentes entornos: Cliente (CSR) y Servidor (SSR)
        </sca-alert>

        <div class="utilities-grid">
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="web" size="32px" />
            <span scaCardClaimTitle>SeoBrowserStore (Cliente/CSR)</span>
            <span scaCardClaimDate>
              • Carga configuración desde archivo JSON vía HTTP<br>
              • Escucha eventos de navegación del Router<br>
              • Actualiza meta tags automáticamente por ruta
            </span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="dns" size="32px" />
            <span scaCardClaimTitle>SeoServerStore (Servidor/SSR)</span>
            <span scaCardClaimDate>
              • Carga configuración desde filesystem (Node.js)<br>
              • Actualiza meta tags durante renderizado SSR<br>
              • Método manual <code>updateSeoForRoute(path)</code>
            </span>
          </sca-card-claim>
        </div>

        <div style="margin-top: 2rem;">
          <sca-view-more [collapsedText]="'Ver Provider Configuration'" [expandedText]="'Ocultar Provider'" [isExpanded]="false">
            <pre class="code-example">{{ providerCode }}</pre>
          </sca-view-more>
        </div>
      </section>

      <!-- Signals del Store -->
      <section class="card">
        <h2>Signals del Store</h2>
        
        <div class="utilities-grid">
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="toggle_on" size="24px" />
            <span scaCardClaimTitle>config$()</span>
            <span scaCardClaimDate>
              <code>SeoData | null</code><br>
              Configuración SEO completa cargada
            </span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="toggle_on" size="24px" />
            <span scaCardClaimTitle>phase$()</span>
            <span scaCardClaimDate>
              <code>CommonPhase</code><br>
              Fase actual: IDLE, LOADING, LOADED, ERROR
            </span>
          </sca-card-claim>
          
          <sca-card-claim>
            <sca-icon scaCardClaimIcon icon="toggle_on" size="24px" />
            <span scaCardClaimTitle>error$()</span>
            <span scaCardClaimDate>
              <code>unknown | null</code><br>
              Error si fase es ERROR
            </span>
          </sca-card-claim>
        </div>
      </section>

      <!-- Estructura JSON -->
      <section class="card">
        <h2>Estructura del JSON de Configuración</h2>
        
        <sca-alert variant="success" style="margin-bottom: 1.5rem;">
          <sca-icon icon="check_circle" size="24px" [weight]="300" left />
          <strong>Features:</strong> Configuración por ruta con wildcards, sustitución de parámetros dinámicos, Open Graph y Twitter Cards, Meta robots, canonical, viewport
        </sca-alert>

        <sca-view-more [collapsedText]="'Ver Estructura JSON'" [expandedText]="'Ocultar JSON'" [isExpanded]="false">
          <pre class="code-example">{{ jsonStructure }}</pre>
        </sca-view-more>
      </section>
    </div>
  `,
  styles: [`
    .container { 
      padding: 0 2rem; 
      margin: 0 auto; 
          @media (max-width: 1400px) {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
    }
    
    .card { 
      background: white; 
      border-radius: 8px; 
      padding: 2rem; 
      margin-bottom: 2rem; 
      box-shadow: 0 2px 4px rgba(0,0,0,0.1); 
    }
    
    .card h2 { 
      color: #333; 
      margin-bottom: 1.5rem; 
      padding-bottom: 0.5rem; 
    }
    
    .utilities-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
      margin: 1rem 0;
    }
    
    code {
      background: #f5f5f5;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9rem;
    }
    
    pre.code-example {
      background: #263238;
      color: #aed581;
      padding: 0;
      border-radius: 8px;
      overflow-x: auto;
      line-height: 1.6;
      font-family: 'Courier New', monospace;
      margin: 0;
    }
    
    .title-test {
      margin-bottom: 2rem;
    }
  `]
})
export class SeoTestComponent {
  readonly seoStore = inject(SeoBrowserStore);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);

  readonly providerCode = `// En app.config.ts
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserSeo('/assets/seo/seo.json')
  ]
};`;

  readonly jsonStructure = `{
  "version": "1.0.0",
  "default": {
    "title": "Mi App",
    "description": "Descripción por defecto",
    "keywords": "angular, seo",
    "metaTags": []
  },
  "pages": {
    "/home": {
      "title": "Home | Mi App",
      "description": "Página principal",
      "ogType": "website",
      "image": "/assets/images/og-home.jpg",
      "meta": []
    }
  }
}`;

  currentTitle(): string {
    return this.title.getTitle();
  }

  getMetaContent(name: string): string | null {
    const tag = this.meta.getTag(`name='${name}'`);
    return tag ? tag.content : null;
  }
}